import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Printer, Save, User, CalendarDays, DollarSign, FileText, BedDouble } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import FolioModal from './FolioModal';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const ReservationEditModal = ({ open, onClose, reservationId, propertyId, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reservation, setReservation] = useState(null);
  const [guest, setGuest] = useState(null);
  const [units, setUnits] = useState([]);
  const [showFolio, setShowFolio] = useState(false);
  const [activeTab, setActiveTab] = useState('stay');

  // Editable reservation fields
  const [form, setForm] = useState({
    check_in: '', check_out: '', unit_id: '', adults: 1, children: 0,
    stay_type: 'overnight', base_amount: 0, tax_amount: 0, notes: '', guest_note: ''
  });

  // Editable guest fields
  const [guestForm, setGuestForm] = useState({
    name: '', email: '', phone: '', address: '', city: '', state: '', zip_code: '',
    document_type: '', document_number: ''
  });

  const token = localStorage.getItem('access_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (open && reservationId) {
      fetchData();
      setActiveTab('stay');
    }
  }, [open, reservationId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resResp, unitsResp] = await Promise.all([
        axios.get(`${API}/reservations/${reservationId}`, { headers }),
        propertyId ? axios.get(`${API}/properties/${propertyId}/units`, { headers }) : Promise.resolve({ data: [] })
      ]);

      const res = resResp.data;
      setReservation(res);
      setForm({
        check_in: res.check_in || '',
        check_out: res.check_out || '',
        unit_id: res.unit_id || '',
        adults: res.adults || 1,
        children: res.children || 0,
        stay_type: res.stay_type || 'overnight',
        base_amount: res.base_amount || 0,
        tax_amount: res.tax_amount || 0,
        notes: res.notes || '',
        guest_note: res.guest_note || ''
      });

      setUnits(unitsResp.data || []);

      // Fetch guest details
      if (res.guest_id) {
        const gResp = await axios.get(`${API}/guests/${res.guest_id}`, { headers });
        const g = gResp.data;
        setGuest(g);
        setGuestForm({
          name: g.name || '', email: g.email || '', phone: g.phone || '',
          address: g.address || '', city: g.city || '', state: g.state || '',
          zip_code: g.zip_code || '', document_type: g.document_type || '',
          document_number: g.document_number || ''
        });
      }
    } catch (err) {
      console.error('Error loading reservation:', err);
      toast.error('Failed to load reservation');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Update reservation
      const resDiff = {};
      if (form.check_in !== reservation.check_in) resDiff.check_in = form.check_in;
      if (form.check_out !== reservation.check_out) resDiff.check_out = form.check_out;
      if (form.unit_id !== reservation.unit_id) resDiff.unit_id = form.unit_id;
      if (form.adults !== reservation.adults) resDiff.adults = parseInt(form.adults);
      if (form.children !== reservation.children) resDiff.children = parseInt(form.children);
      if (form.stay_type !== reservation.stay_type) resDiff.stay_type = form.stay_type;
      if (parseFloat(form.base_amount) !== reservation.base_amount) resDiff.base_amount = parseFloat(form.base_amount);
      if (parseFloat(form.tax_amount) !== reservation.tax_amount) resDiff.tax_amount = parseFloat(form.tax_amount);
      if (form.notes !== (reservation.notes || '')) resDiff.notes = form.notes;
      if (form.guest_note !== (reservation.guest_note || '')) resDiff.guest_note = form.guest_note;

      // Update guest
      const guestDiff = {};
      if (guest) {
        if (guestForm.name !== guest.name) guestDiff.name = guestForm.name;
        if (guestForm.email !== (guest.email || '')) guestDiff.email = guestForm.email;
        if (guestForm.phone !== (guest.phone || '')) guestDiff.phone = guestForm.phone;
        if (guestForm.address !== (guest.address || '')) guestDiff.address = guestForm.address;
        if (guestForm.city !== (guest.city || '')) guestDiff.city = guestForm.city;
        if (guestForm.state !== (guest.state || '')) guestDiff.state = guestForm.state;
        if (guestForm.zip_code !== (guest.zip_code || '')) guestDiff.zip_code = guestForm.zip_code;
        if (guestForm.document_type !== (guest.document_type || '')) guestDiff.document_type = guestForm.document_type;
        if (guestForm.document_number !== (guest.document_number || '')) guestDiff.document_number = guestForm.document_number;
      }

      const promises = [];
      if (Object.keys(resDiff).length > 0) {
        promises.push(axios.patch(`${API}/reservations/${reservationId}`, resDiff, { headers }));
      }
      if (guest && Object.keys(guestDiff).length > 0) {
        promises.push(axios.patch(`${API}/guests/${guest.id}`, guestDiff, { headers }));
      }

      if (promises.length === 0) {
        toast.info('No changes to save');
        setSaving(false);
        return;
      }

      await Promise.all(promises);
      toast.success('Reservation updated');
      onSaved?.();
      onClose();
    } catch (err) {
      console.error('Error saving:', err);
      toast.error(err.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const isReadOnly = reservation && ['CHECKED_OUT', 'CANCELLED'].includes(reservation.status);
  const imgUrl = guest?.id_image_url ? `${BACKEND}${guest.id_image_url}` : null;
  const unitName = units.find(u => u.id === form.unit_id)?.name || reservation?.unit_id || 'N/A';

  if (!open) return null;

  const tabs = [
    { id: 'stay', label: 'Stay Details', icon: CalendarDays },
    { id: 'guest', label: 'Guest Info', icon: User },
    { id: 'billing', label: 'Billing', icon: DollarSign },
  ];

  return (
    <>
      <Dialog open={open && !showFolio} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="reservation-edit-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{isReadOnly ? 'Reservation Details' : 'Edit Reservation'}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setShowFolio(true)} data-testid="edit-modal-print-folio-btn">
                  <Printer size={14} className="mr-1" /> Print Folio
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="text-center py-12 text-slate-500">Loading...</div>
          ) : reservation ? (
            <div className="space-y-4">
              {/* Status Badge & Room Summary */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid="edit-modal-summary">
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                    reservation.status === 'CONFIRMED' ? 'bg-blue-100 text-blue-800' :
                    reservation.status === 'IN_HOUSE' ? 'bg-emerald-100 text-emerald-800' :
                    reservation.status === 'CHECKED_OUT' ? 'bg-slate-100 text-slate-800' :
                    'bg-red-100 text-red-800'
                  }`} data-testid="edit-modal-status">{reservation.status}</span>
                  <span className="font-medium text-slate-900 flex items-center gap-1">
                    <BedDouble size={14} /> Room {unitName}
                  </span>
                </div>
                <div className="text-sm text-slate-500">
                  Confirmation #{reservationId?.slice(0, 8)}
                </div>
              </div>

              {/* ID Photo */}
              {imgUrl && (
                <div className="p-3 border rounded-lg" data-testid="edit-modal-id-photo">
                  <p className="text-xs font-semibold text-slate-500 mb-2">Guest ID Photo</p>
                  <img src={imgUrl} alt="Guest ID" className="max-h-32 rounded border" onError={(e) => { e.target.style.display = 'none'; }} />
                </div>
              )}

              {/* Tabs */}
              <div className="flex border-b" data-testid="edit-modal-tabs">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-cyan-600 text-cyan-700'
                        : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                    data-testid={`edit-modal-tab-${tab.id}`}
                  >
                    <tab.icon size={14} />
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab Content: Stay Details */}
              {activeTab === 'stay' && (
                <div className="grid grid-cols-2 gap-4" data-testid="edit-modal-stay-tab">
                  <div>
                    <Label>Check-In</Label>
                    <Input type="date" value={form.check_in} disabled={isReadOnly}
                      onChange={e => setForm(f => ({ ...f, check_in: e.target.value }))}
                      data-testid="edit-check-in" />
                  </div>
                  <div>
                    <Label>Check-Out</Label>
                    <Input type="date" value={form.check_out} disabled={isReadOnly}
                      onChange={e => setForm(f => ({ ...f, check_out: e.target.value }))}
                      data-testid="edit-check-out" />
                  </div>
                  <div>
                    <Label>Room</Label>
                    <Select value={form.unit_id} onValueChange={v => setForm(f => ({ ...f, unit_id: v }))} disabled={isReadOnly}>
                      <SelectTrigger data-testid="edit-room-select">
                        <SelectValue placeholder="Select room" />
                      </SelectTrigger>
                      <SelectContent>
                        {units.map(u => (
                          <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Stay Type</Label>
                    <Select value={form.stay_type} onValueChange={v => setForm(f => ({ ...f, stay_type: v }))} disabled={isReadOnly}>
                      <SelectTrigger data-testid="edit-stay-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="overnight">Overnight</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="day_use">Day Use</SelectItem>
                        <SelectItem value="hourly">Hourly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Adults</Label>
                    <Input type="number" min={1} value={form.adults} disabled={isReadOnly}
                      onChange={e => setForm(f => ({ ...f, adults: parseInt(e.target.value) || 1 }))}
                      data-testid="edit-adults" />
                  </div>
                  <div>
                    <Label>Children</Label>
                    <Input type="number" min={0} value={form.children} disabled={isReadOnly}
                      onChange={e => setForm(f => ({ ...f, children: parseInt(e.target.value) || 0 }))}
                      data-testid="edit-children" />
                  </div>
                  <div className="col-span-2">
                    <Label>Notes</Label>
                    <textarea
                      className="w-full border rounded-md p-2 text-sm min-h-[60px] resize-y"
                      value={form.notes}
                      disabled={isReadOnly}
                      onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                      data-testid="edit-notes"
                      placeholder="Check-in notes, special requests, etc."
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Guest Note <span className="text-xs text-slate-400 font-normal">(internal, shows on folio)</span></Label>
                    <textarea
                      className="w-full border rounded-md p-2 text-sm min-h-[60px] resize-y"
                      value={form.guest_note}
                      disabled={isReadOnly}
                      onChange={e => setForm(f => ({ ...f, guest_note: e.target.value }))}
                      data-testid="edit-guest-note"
                      placeholder="VIP, preferences, internal remarks..."
                    />
                  </div>
                </div>
              )}

              {/* Tab Content: Guest Info */}
              {activeTab === 'guest' && (
                <div className="grid grid-cols-2 gap-4" data-testid="edit-modal-guest-tab">
                  <div className="col-span-2">
                    <Label>Name</Label>
                    <Input value={guestForm.name} disabled={isReadOnly}
                      onChange={e => setGuestForm(f => ({ ...f, name: e.target.value }))}
                      data-testid="edit-guest-name" />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input value={guestForm.phone} disabled={isReadOnly}
                      onChange={e => setGuestForm(f => ({ ...f, phone: e.target.value }))}
                      data-testid="edit-guest-phone" />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input value={guestForm.email} disabled={isReadOnly}
                      onChange={e => setGuestForm(f => ({ ...f, email: e.target.value }))}
                      data-testid="edit-guest-email" />
                  </div>
                  <div className="col-span-2">
                    <Label>Address</Label>
                    <Input value={guestForm.address} disabled={isReadOnly}
                      onChange={e => setGuestForm(f => ({ ...f, address: e.target.value }))}
                      data-testid="edit-guest-address" />
                  </div>
                  <div>
                    <Label>City</Label>
                    <Input value={guestForm.city} disabled={isReadOnly}
                      onChange={e => setGuestForm(f => ({ ...f, city: e.target.value }))}
                      data-testid="edit-guest-city" />
                  </div>
                  <div>
                    <Label>State</Label>
                    <Input value={guestForm.state} disabled={isReadOnly}
                      onChange={e => setGuestForm(f => ({ ...f, state: e.target.value }))}
                      data-testid="edit-guest-state" />
                  </div>
                  <div>
                    <Label>Zip Code</Label>
                    <Input value={guestForm.zip_code} disabled={isReadOnly}
                      onChange={e => setGuestForm(f => ({ ...f, zip_code: e.target.value }))}
                      data-testid="edit-guest-zip" />
                  </div>
                  <div>
                    <Label>Document Type</Label>
                    <Select value={guestForm.document_type || '_none'} disabled={isReadOnly}
                      onValueChange={v => setGuestForm(f => ({ ...f, document_type: v === '_none' ? '' : v }))}>
                      <SelectTrigger data-testid="edit-guest-doc-type">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">None</SelectItem>
                        <SelectItem value="DRIVERS_LICENSE">Driver's License</SelectItem>
                        <SelectItem value="PASSPORT">Passport</SelectItem>
                        <SelectItem value="NATIONAL_ID">National ID</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Document Number</Label>
                    <Input value={guestForm.document_number} disabled={isReadOnly}
                      onChange={e => setGuestForm(f => ({ ...f, document_number: e.target.value }))}
                      data-testid="edit-guest-doc-number" />
                  </div>

                  {/* ID Photo Upload */}
                  {guest && (
                    <div className="col-span-2 border-t pt-3 mt-2">
                      <Label>ID Photo</Label>
                      {guest.id_image_url ? (
                        <div className="mt-1 space-y-2">
                          <img src={`${BACKEND}${guest.id_image_url}`} alt="Guest ID" className="max-h-32 rounded border" data-testid="edit-guest-id-photo" onError={(e) => { e.target.style.display='none'; }} />
                          <p className="text-xs text-green-600 font-medium">ID photo on file</p>
                        </div>
                      ) : (
                        <p className="text-xs text-amber-600 mt-1">No ID photo on file</p>
                      )}
                      {!isReadOnly && (
                        <div className="mt-2">
                          <input type="file" accept="image/*" capture="environment" data-testid="edit-guest-id-upload"
                            className="text-sm file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:bg-cyan-50 file:text-cyan-700 hover:file:bg-cyan-100"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              if (file.size < 100) { toast.error('Photo appears empty. Please retake.'); return; }
                              try {
                                const fd = new FormData();
                                fd.append('file', file);
                                await axios.post(`${API}/guests/${guest.id}/upload-id-image`, fd, { headers: { Authorization: `Bearer ${token}` } });
                                const gRes = await axios.get(`${API}/guests/${guest.id}`, { headers: { Authorization: `Bearer ${token}` } });
                                setGuest(gRes.data);
                                toast.success('ID photo uploaded successfully');
                              } catch (err) { toast.error('Failed to upload ID photo'); }
                            }}
                          />
                          <p className="text-xs text-slate-400 mt-1">Upload or take a photo of the guest's ID</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Tab Content: Billing */}
              {activeTab === 'billing' && (
                <div className="space-y-4" data-testid="edit-modal-billing-tab">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Room Charge ($)</Label>
                      <Input type="number" step="0.01" min={0} value={form.base_amount} disabled={isReadOnly}
                        onChange={e => setForm(f => ({ ...f, base_amount: e.target.value }))}
                        data-testid="edit-base-amount" />
                    </div>
                    <div>
                      <Label>Tax ($)</Label>
                      <Input type="number" step="0.01" min={0} value={form.tax_amount} disabled={isReadOnly}
                        onChange={e => setForm(f => ({ ...f, tax_amount: e.target.value }))}
                        data-testid="edit-tax-amount" />
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Room Charge:</span>
                      <span className="font-medium">${parseFloat(form.base_amount || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-slate-600">Tax:</span>
                      <span className="font-medium">${parseFloat(form.tax_amount || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between mt-2 pt-2 border-t font-semibold text-base">
                      <span>Total:</span>
                      <span>${(parseFloat(form.base_amount || 0) + parseFloat(form.tax_amount || 0)).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Save / Cancel */}
              {!isReadOnly && (
                <div className="flex justify-end gap-2 pt-2 border-t" data-testid="edit-modal-actions">
                  <Button variant="outline" onClick={onClose}>Cancel</Button>
                  <Button onClick={handleSave} disabled={saving} data-testid="edit-modal-save-btn">
                    <Save size={14} className="mr-1" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-500">No reservation data</div>
          )}
        </DialogContent>
      </Dialog>

      {/* Folio Print Modal */}
      <FolioModal
        open={showFolio}
        onClose={() => setShowFolio(false)}
        reservationId={reservationId}
      />
    </>
  );
};

export default ReservationEditModal;
